import type { AppRouter } from '@alison/api/router';
import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import type { TRPCClientErrorLike } from '@trpc/client';
import type { inferInput, inferOutput } from '@trpc/tanstack-react-query';
import { useMemo } from 'react';
import { useTRPC } from '../../../../lib/trpc.js';
import { useMetricCatalog } from '../../data/api.js';
import { detailKpis } from '../../data/displayedMetrics.js';

type TRPC = ReturnType<typeof useTRPC>;

type QueryResult<TData> = UseQueryResult<TData, TRPCClientErrorLike<AppRouter>>;

export type CreativeDetailScope = Omit<
  inferInput<TRPC['singleCreative']['performance']>,
  'creativeId' | 'kpis'
>;

export type PerformanceOutput = inferOutput<TRPC['singleCreative']['performance']>;
export type BenchmarkedMetric = PerformanceOutput['metrics'][number];

export function useDetailKpis(accountIds: number[] | undefined, leadingKpi: string): string[] {
  const { data: catalog } = useMetricCatalog(accountIds);
  return useMemo(() => detailKpis(catalog, leadingKpi), [catalog, leadingKpi]);
}

export function useSingleCreativePerformance(
  scope: CreativeDetailScope & { creativeId: string; kpis: string[] },
  enabled: boolean,
): QueryResult<PerformanceOutput> {
  const trpc = useTRPC();
  return useQuery({ ...trpc.singleCreative.performance.queryOptions(scope), enabled });
}

export type CreativeTrendScope = Omit<
  inferInput<TRPC['creatives']['metricTrend']>,
  'metrics' | 'creativeId' | 'node'
>;
export type CreativeTrendOutput = inferOutput<TRPC['creatives']['metricTrend']>;
export type CreativeTrendSeries = CreativeTrendOutput['metrics'][number];
export type DeliveryNode = inferInput<TRPC['creatives']['metricTrend']>['node'];

export function useCreativeTrend(
  scope: CreativeTrendScope & {
    creativeId: string;
    node?: DeliveryNode;
    metrics: { name: string; aggregation: 'total' | 'average' }[];
  },
  enabled: boolean,
): QueryResult<CreativeTrendOutput> {
  const trpc = useTRPC();
  return useQuery({ ...trpc.creatives.metricTrend.queryOptions(scope), enabled });
}

export type SpendBreakdownScope = Omit<
  inferInput<TRPC['singleCreative']['spendBreakdown']>,
  'creativeId' | 'kpis' | 'dimension'
>;
export type SpendBreakdownOutput = inferOutput<TRPC['singleCreative']['spendBreakdown']>;
export type BreakdownDimension = SpendBreakdownOutput['dimension'];

export function useSpendBreakdown(
  scope: SpendBreakdownScope & {
    creativeId: string;
    kpis: string[];
    dimension: BreakdownDimension;
  },
  enabled: boolean,
): QueryResult<SpendBreakdownOutput> {
  const trpc = useTRPC();
  return useQuery({ ...trpc.singleCreative.spendBreakdown.queryOptions(scope), enabled });
}

export type DeliveryScope = Omit<
  inferInput<TRPC['singleCreative']['delivery']>,
  'creativeId' | 'kpis'
>;
export type DeliveryOutput = inferOutput<TRPC['singleCreative']['delivery']>;

export function useDelivery(
  scope: DeliveryScope & { creativeId: string; kpis: string[] },
  enabled: boolean,
): QueryResult<DeliveryOutput> {
  const trpc = useTRPC();
  return useQuery({ ...trpc.singleCreative.delivery.queryOptions(scope), enabled });
}

export type ScoreOutput = inferOutput<TRPC['singleCreative']['score']>;
export type ScoreSeries = ScoreOutput['series'][number];
export type ScoreSeriesName = ScoreSeries['name'];

export function useSingleCreativeScore(
  scope: CreativeDetailScope & { creativeId: string },
  enabled: boolean,
): QueryResult<ScoreOutput> {
  const trpc = useTRPC();
  return useQuery({ ...trpc.singleCreative.score.queryOptions(scope), enabled });
}

export type CompletionCurveOutput = inferOutput<TRPC['singleCreative']['completionCurve']>;

export function useCompletionCurve(
  scope: CreativeDetailScope & { creativeId: string },
  enabled: boolean,
): QueryResult<CompletionCurveOutput> {
  const trpc = useTRPC();
  return useQuery({ ...trpc.singleCreative.completionCurve.queryOptions(scope), enabled });
}

export type TagsOutput = inferOutput<TRPC['singleCreative']['tags']>;
export type TagCategory = TagsOutput['categories'][number];

export function useSingleCreativeTags(
  scope: CreativeDetailScope & { creativeId: string },
  enabled: boolean,
): QueryResult<TagsOutput> {
  const trpc = useTRPC();
  return useQuery({ ...trpc.singleCreative.tags.queryOptions(scope), enabled });
}
